import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Snowflake, CheckCircle2, ArrowRight, Phone, Mail,
  Star, ShieldCheck, Zap, Clock, MapPin, ChevronRight
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import './Servicepage.css';

import imgClim from '../assets/climService.jpg';
import logoRGE from '../assets/logo-qualipac-RGE.PNG';

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Installation Climatisation Île-de-France",
  "provider": {
    "@type": "LocalBusiness",
    "name": "SOCLIMATE",
    "telephone": "+33618011895",
    "email": "soclimate.95@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1 rue de Belloy",
      "addressLocality": "Villaines-sous-Bois",
      "postalCode": "95570",
      "addressCountry": "FR"
    },
    "areaServed": "Île-de-France",
    "priceRange": "€€"
  },
  "description": "Installation, maintenance et dépannage de systèmes de climatisation en Île-de-France. Certifié RGE QualiPAC.",
  "serviceType": "Installation Climatisation"
};

const PRESTATIONS = [
  { title: 'Split mural', desc: 'Système le plus répandu, discret et performant pour une pièce.' },
  { title: 'Multi-split', desc: 'Une unité extérieure pour plusieurs intérieures — idéal pour les appartements.' },
  { title: 'Gainable', desc: 'Entièrement dissimulé dans vos faux plafonds, silence absolu.' },
  { title: 'Cassette plafond', desc: 'Diffusion 4 directions pour les grands espaces professionnels.' },
  { title: 'Console', desc: 'Solution posée au sol ou haute, flexible et économique.' },
  { title: 'Étude de dimensionnement', desc: 'Calcul précis de la puissance nécessaire selon votre superficie.' },
];

const AVANTAGES = [
  { icon: <Zap size={20} strokeWidth={1.8} />, title: 'Confort été & hiver', desc: 'Chaleur en hiver, fraîcheur en été avec un seul appareil réversible.' },
  { icon: <ShieldCheck size={20} strokeWidth={1.8} />, title: 'Installation certifiée', desc: 'Techniciens certifiés, travaux conformes aux normes NF EN 378.' },
  { icon: <Clock size={20} strokeWidth={1.8} />, title: 'Pose en 1 journée', desc: 'La majorité des installations est finalisée en une seule journée.' },
  { icon: <Star size={20} strokeWidth={1.8} />, title: 'Garantie constructeur', desc: 'Jusqu\'à 5 ans de garantie selon les marques installées.' },
];

const FAQ = [
  { q: 'Quel est le prix d\'une installation de climatisation en Île-de-France ?', a: 'Le coût varie entre 1 500 € et 4 500 € selon le type de système, la puissance et la complexité de l\'installation. Nous vous fournissons un devis gratuit et personnalisé.' },
  { q: 'Faut-il une autorisation pour installer une climatisation ?', a: 'Pour les maisons individuelles, une déclaration de travaux peut être nécessaire si l\'unité extérieure est visible. Nous vous accompagnons dans ces démarches.' },
  { q: 'Quelle marque de climatisation installez-vous ?', a: 'Nous travaillons avec les grandes marques : Mitsubishi Electric, Daikin, Toshiba, Samsung, Panasonic. Nous conseillons la solution la plus adaptée à votre projet.' },
  { q: 'Intervenez-vous pour les professionnels ?', a: 'Oui, nous équipons bureaux, commerces, restaurants, hôtels et toute structure professionnelle en Île-de-France.' },
];

export default function Climatisation() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Helmet>
        <title>Installation Climatisation Île-de-France | SOCLIMATE</title>
        <meta name="description" content="Expert en installation de climatisation en Île-de-France. Split, multi-split, gainable. Certifié RGE. Devis gratuit — intervention rapide 95, 75, 78, 91, 92, 93, 94." />
        <meta name="keywords" content="climatisation ile de france, installation climatisation, split mural, climatiseur reversible, pose climatisation, installateur climatisation 95" />
        <link rel="canonical" href="https://www.soclimate.fr/climatisation" />
        <meta property="og:title" content="Installation Climatisation Île-de-France | SOCLIMATE" />
        <meta property="og:description" content="Expert en installation de climatisation en Île-de-France. Certifié RGE. Devis gratuit." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(SCHEMA)}</script>
      </Helmet>

      <Navbar />

      <main className="sp-page">

        {/* ── HERO ── */}
        <section className="sp-hero sp-hero--blue">
          <div className="sp-hero__bg" style={{ backgroundImage: `url(${imgClim})` }} />
          <div className="sp-hero__veil" />
          <div className="container sp-hero__inner">
            <div className="sp-hero__breadcrumb">
              <Link to="/">Accueil</Link>
              <ChevronRight size={14} />
              <Link to="/solution">Services</Link>
              <ChevronRight size={14} />
              <span>Climatisation</span>
            </div>
            <div className="sp-hero__badge">
              <Snowflake size={16} strokeWidth={2} />
              Installation · Maintenance · Dépannage
            </div>
            <h1 className="sp-hero__h1">
              Installation Climatisation<br />
              <span>Île-de-France</span>
            </h1>
            <p className="sp-hero__sub">
              Spécialiste de l'installation de climatisation depuis plus de 10 ans.
              Certifié RGE, nous intervenons dans tout le Val-d'Oise, Paris
              et la grande couronne.
            </p>
            <div className="sp-hero__ctas">
              <a href="tel:0618011895" className="sp-cta-primary">
                <Phone size={16} strokeWidth={2} /> 06 18 01 18 95
              </a>
              <a href="#contact" className="sp-cta-ghost">
                Devis gratuit <ArrowRight size={15} strokeWidth={2} />
              </a>
            </div>
            
          </div>
        </section>

        {/* ── INTRO SEO ── */}
        <section className="sp-intro">
          <div className="container sp-intro__grid">
            <div className="sp-intro__text">
              <h2>Votre expert en climatisation en Île-de-France</h2>
              <p>
                SOCLIMATE est une entreprise spécialisée dans l'installation de systèmes
                de climatisation pour les particuliers et les professionnels en Île-de-France.
                Nous intervenons dans le <strong>Val-d'Oise (95)</strong>, Paris (75),
                les Yvelines (78), l'Essonne (91), les Hauts-de-Seine (92),
                la Seine-Saint-Denis (93) et le Val-de-Marne (94).
              </p>
              <p>
                Nos techniciens certifiés <strong>RGE QualiPAC</strong> assurent
                une installation propre, conforme aux normes en vigueur et optimisée
                pour votre consommation énergétique.
              </p>
              <ul className="sp-check-list">
                <li><CheckCircle2 size={16} strokeWidth={2} />Devis gratuit sous 24h</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Installation en 1 journée</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Garantie jusqu'à 5 ans</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Accompagnement aides de l'État</li>
              </ul>
            </div>
            <div className="sp-intro__rge">
              <img src={logoRGE} alt="Certification RGE QualiPAC SOCLIMATE" className="sp-intro__rge-logo" />
              <p>Certification qui vous donne accès à <strong>MaPrimeRénov'</strong>, CEE et TVA à 5,5 %</p>
            </div>
          </div>
        </section>

        {/* ── PRESTATIONS ── */}
        <section className="sp-section sp-section--gray">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />NOS PRESTATIONS</p>
              <h2>Tous types de climatisation<br /><em>installés par nos équipes</em></h2>
            </div>
            <div className="sp-grid-3">
              {PRESTATIONS.map((p, i) => (
                <div key={i} className="sp-presta-card">
                  <div className="sp-presta-card__num">0{i + 1}</div>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── AVANTAGES ── */}
        <section className="sp-section">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />POURQUOI CHOISIR SOCLIMATE</p>
              <h2>Une installation <em>professionnelle</em><br />et garantie</h2>
            </div>
            <div className="sp-grid-2">
              {AVANTAGES.map((a, i) => (
                <div key={i} className="sp-avantage">
                  <div className="sp-avantage__icon">{a.icon}</div>
                  <div>
                    <h3>{a.title}</h3>
                    <p>{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── ZONES ── */}
        <section className="sp-section sp-section--gray">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />ZONES D'INTERVENTION</p>
              <h2>Installation climatisation<br /><em>dans toute l'Île-de-France</em></h2>
            </div>
            <div className="sp-zones">
              {['Val-d\'Oise (95)', 'Paris (75)', 'Yvelines (78)', 'Essonne (91)', 'Hauts-de-Seine (92)', 'Seine-Saint-Denis (93)', 'Val-de-Marne (94)', 'Seine-et-Marne (77)'].map((z, i) => (
                <div key={i} className="sp-zone-pill">
                  <MapPin size={13} strokeWidth={2} /> {z}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ SEO ── */}
        <section className="sp-section">
          <div className="container sp-faq-wrap">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />FAQ</p>
              <h2>Questions fréquentes<br /><em>sur la climatisation</em></h2>
            </div>
            <div className="sp-faq">
              {FAQ.map((item, i) => (
                <details key={i} className="sp-faq__item">
                  <summary className="sp-faq__q">{item.q}</summary>
                  <p className="sp-faq__a">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA FINAL ── */}
        <section className="sp-cta-band">
          <div className="container sp-cta-band__inner">
            <div>
              <h2>Besoin d'une installation de climatisation ?</h2>
              <p>Contactez-nous pour un devis gratuit en Île-de-France.</p>
            </div>
            <div className="sp-cta-band__btns">
              <a href="tel:0618011895" className="sp-cta-primary">
                <Phone size={16} strokeWidth={2} /> 06 18 01 18 95
              </a>
              <a href="mailto:soclimate.95@gmail.com" className="sp-cta-ghost sp-cta-ghost--light">
                <Mail size={16} strokeWidth={2} /> Email
              </a>
            </div>
          </div>
        </section>

      </main>

      <Contact />
      <Footer />
    </>
  );
}